import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Edit, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import AdminNav from './AdminNav';

const AdminPrompts = () => {
  const { t } = useTranslation();
  const { getAuthHeaders, logout } = useAuth();
  const [prompts, setPrompts] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    content: '',
    description: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleteTargetName, setDeleteTargetName] = useState('');

  useEffect(() => {
    fetchPrompts();
  }, []);

  const fetchPrompts = async () => {
    try {
      const response = await fetch('/api/admin/prompts', {
        headers: getAuthHeaders()
      });

      if (response.status === 401) {
        logout();
        return;
      }

      if (!response.ok) {
        setPrompts([]);
        return;
      }

      const data = await response.json();
      setPrompts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching prompts:', err);
      setPrompts([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({ name: '', content: '', description: '' });
    setIsCreating(false);
    setEditingId(null);
  };

  const handleCreate = () => {
    resetForm();
    setIsCreating(true);
  };

  const handleEdit = (prompt) => {
    setFormData({
      name: prompt.name,
      content: prompt.content,
      description: prompt.description || ''
    });
    setEditingId(prompt.id);
    setIsCreating(false);
  };

  const handleCancel = () => {
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.content.trim()) {
      window.alert(t('admin.prompts.nameAndContentRequired'));
      return;
    }

    setIsLoading(true);
    try {
      const url = editingId
        ? `/api/admin/prompts/${editingId}`
        : '/api/admin/prompts';
      const method = editingId ? 'PUT' : 'POST';
      const body = JSON.stringify({
        name: formData.name.trim(),
        content: formData.content.trim(),
        description: formData.description.trim() || null
      });

      const response = await fetch(url, {
        method,
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body
      });

      if (response.status === 401) {
        logout();
        return;
      }

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        window.alert(data.error || t('admin.prompts.errorSaving'));
        return;
      }

      await fetchPrompts();
      resetForm();
    } catch (err) {
      console.error('Error saving prompt:', err);
      window.alert(t('admin.prompts.errorSaving'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClick = (prompt) => {
    setDeleteTargetId(prompt.id);
    setDeleteTargetName(prompt.name);
    setShowConfirmDelete(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTargetId) return;

    setIsLoading(true);
    try {
      const response = await fetch(`/api/admin/prompts/${deleteTargetId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.status === 401) {
        logout();
        return;
      }

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        window.alert(data.error || t('admin.prompts.errorDeleting'));
        return;
      }

      await fetchPrompts();
      setShowConfirmDelete(false);
      setDeleteTargetId(null);
      setDeleteTargetName('');
    } catch (err) {
      console.error('Error deleting prompt:', err);
      window.alert(t('admin.prompts.errorDeleting'));
    } finally {
      setIsLoading(false);
    }
  };

  const truncateContent = (text, maxLen = 120) => {
    if (!text) return '';
    const trimmed = text.trim();
    if (trimmed.length <= maxLen) return trimmed;
    return trimmed.slice(0, maxLen) + '...';
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900">{t('admin.prompts.title')}</h1>
          <p className="text-gray-600 mt-2">{t('admin.prompts.subtitle')}</p>
        </div>

        <AdminNav />

        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">{t('admin.prompts.yourPrompts')}</h2>
            <button
              type="button"
              onClick={handleCreate}
              disabled={isCreating || editingId}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Plus size={18} />
              {t('admin.prompts.createPrompt')}
            </button>
          </div>

          {prompts.length === 0 && !isCreating && !editingId ? (
            <p className="text-gray-500">{t('admin.prompts.noPromptsYet')}</p>
          ) : (
            <div className="space-y-3">
              {prompts.map((prompt) => (
                <div
                  key={prompt.id}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900">{prompt.name}</h3>
                      {prompt.description && (
                        <p className="text-sm text-gray-600 mt-1">{prompt.description}</p>
                      )}
                      <p className="text-sm text-gray-500 mt-2 font-mono truncate">
                        {truncateContent(prompt.content)}
                      </p>
                      {expandedId === prompt.id && (
                        <pre className="mt-2 p-3 bg-gray-100 rounded text-xs overflow-x-auto whitespace-pre-wrap max-h-48 overflow-y-auto">
                          {prompt.content}
                        </pre>
                      )}
                      <button
                        type="button"
                        onClick={() => setExpandedId(expandedId === prompt.id ? null : prompt.id)}
                        className="text-xs text-blue-600 hover:text-blue-800 mt-1 flex items-center gap-1"
                      >
                        {expandedId === prompt.id ? (
                          <>
                            <ChevronUp size={14} />
                            {t('common.collapse')}
                          </>
                        ) : (
                          <>
                            <ChevronDown size={14} />
                            {t('common.expand')}
                          </>
                        )}
                      </button>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => handleEdit(prompt)}
                        disabled={isCreating}
                        className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded"
                        title={t('admin.prompts.editPrompt')}
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteClick(prompt)}
                        disabled={isCreating}
                        className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded"
                        title={t('admin.prompts.deletePrompt')}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {(isCreating || editingId) && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {editingId ? t('admin.prompts.editPrompt') : t('admin.prompts.createNewPrompt')}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.prompts.name')} *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder={t('admin.prompts.namePlaceholder')}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.prompts.description')}
                </label>
                <input
                  type="text"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder={t('admin.prompts.descriptionPlaceholder')}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.prompts.content')} *
                </label>
                <textarea
                  name="content"
                  value={formData.content}
                  onChange={handleInputChange}
                  required
                  rows="8"
                  placeholder={t('admin.prompts.contentPlaceholder')}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {isLoading ? t('common.saving') : t('common.save')}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={isLoading}
                  className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 disabled:opacity-50"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          </div>
        )}

        {showConfirmDelete && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('admin.prompts.confirmDelete')}
              </h3>
              <p className="text-gray-600 mb-4">
                {t('admin.prompts.confirmDeleteMessage', { name: deleteTargetName })}
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowConfirmDelete(false);
                    setDeleteTargetId(null);
                    setDeleteTargetName('');
                  }}
                  className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleDeleteConfirm}
                  disabled={isLoading}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {t('admin.prompts.deletePrompt')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPrompts;
